import { useEffect, useRef, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { codeExchangePending, subscribeCodeExchange } from "../auth/oidc";
import { useSession } from "../auth/session";
import { useActiveTenantKey } from "../auth/tenantPicker";
import { reconcilePreferredLanguage, toStorableLanguage } from "./profileApi";
import { languageToApply, languageWasStated } from "./preferredLanguage";

// Sign-in reconciliation of the ONE language question CLMS asks (KM-E11 follow-up).
//
// The language a person reads the UI in and the language CLMS writes to them in are two answers to the
// same question, and they live in two places: the SPA's local choice, and `users.preferred_language`
// (which both questionnaire mail paths resolve per recipient). Left alone they drift apart silently —
// the mail leaves days later, in an inbox where nobody can correct it. So the moment a session exists,
// the two are settled against each other, in BOTH directions:
//
//   1. the row has NO preference → it is seeded from the language the person is currently reading CLMS
//      in. Without this, somebody who signs in and never touches the switcher stays null forever and the
//      mail paths keep falling back to the questionnaire's own language;
//   2. the row HAS one → the SPA switches to it. Without this a person whose preference is English signs
//      into a Finnish-defaulting SPA, reads Finnish, and gets English mail, with nothing saying so.
//
// Runs once per signed-in user per TENANT per app load (a restored session counts — the E2E harness and a
// returning browser both arrive that way), and the reconcile call is idempotent: a stored preference is
// never overwritten by the SPA's current language, which may be no more than what the browser reported.
//
// WHY IT WAITS FOR THE TENANT (ADR-0018 §6.2). `users.preferred_language` is a column on a row that is per
// tenant, so this reconciliation is a tenant-scoped WRITE — and for a person whose token carries
// memberships, which tenant it lands in is a question they answer after signing in (auth/tenantPicker.tsx).
// This component is mounted above the router, so the picker's gate — which withholds the ROUTER — cannot
// hold it back; without the wait it would fire the moment a session existed and seed the language on
// whichever tenant the backend resolved from the token alone, in an organisation nobody had chosen yet.
// So it asks `useActiveTenantKey()` first, and keys the guard on the tenant as well as the person: the
// answer arrives for the tenant the person is actually working in, and a session that reaches a second
// tenant reconciles there too rather than being treated as already settled.
//
// For the single-tenant majority the wait is one in-flight read long and changes nothing else: they have no
// memberships to choose between, so the key is settled as soon as that answer arrives.
//
// And if that read FAILS with no choice in force, this reconciliation does not happen on that load at all.
// That is the deliberate price: the failure leaves the app with no answer about which organisation the
// person is in, and reconciling anyway would send the POST with no `X-Tenant` — seeding the language on the
// tenant the token names, which is the pre-selection write this wait exists to prevent, reached by a
// different road. Nothing is lost that matters: the guard below is per LOAD, so the next one reconciles,
// and the two answers this settles drift no further apart in the meantime than they already had.
//
// Direction 2 is weighed against the language the person is reading WHEN THE ANSWER ARRIVES, not when the
// call was made (`languageToApply`). The switcher is in the header of the first screen a session renders,
// so a person can — and in the E2E suite does — change the language inside this round trip; the answer
// that then comes back is about the language they just left, and applying it would undo their choice on
// screen. Their later statement wins, and the switcher has already written it to the row.
//
// Since this reconciliation waits for the tenant it also goes out LATER than the load, so the switch can
// come BEFORE the call rather than inside it — and then the two languages agree and only the row is behind
// (the switcher's `PUT` is a separate request that need not have committed). That is why the decision is
// given `languageWasStated()` as well: a statement made anywhere in this document outranks this answer,
// whichever side of the call it was made. Task 84dd2eef — it is what reverted the change-observation
// sweeps to Finnish mid-pass.
//
// A failure is silent and NOT sticky: the guard is released so the next load tries again. Nothing is
// claimed to the person here — this is not the surface where they set anything (that is the switcher,
// which does report a write that did not land).
export function PreferredLanguageSync() {
  const { i18n } = useTranslation();
  const { session } = useSession();
  const tenantKey = useActiveTenantKey();
  const reconciledFor = useRef<string | null>(null);

  const userId = session?.userId ?? null;

  // SUBSCRIBED, not merely asked (auth/oidc.ts `subscribeCodeExchange`). The deferral below has to be
  // RELEASED by something, and nothing else here changes when the exchange finishes: the callback
  // establishes the session for the same person this document booted on and navigates inside it, so
  // `userId` and `tenantKey` are the values they already were. Reading the answer through a store instead
  // makes the settlement a re-render, which is what runs the effect again — on this document, on the token
  // the exchange delivered. Without it the deferral is permanent and this reconciliation is simply not
  // made, which is what task 2625efef's first round shipped.
  const exchangePending = useSyncExternalStore(subscribeCodeExchange, codeExchangePending);

  useEffect(() => {
    if (!userId || tenantKey === null) {
      // Signed out, or the tenant is still an open question: the next sign-in — the same person or another
      // — reconciles again, and so does the moment the tenant is settled.
      reconciledFor.current = null;
      return;
    }
    // NOT YET, ON A CALLBACK WHOSE CODE IS STILL UNSPENT (auth/oidc.ts `codeExchangePending`). This
    // component sits above the router, so it mounts on the OIDC callback too — on the session the PREVIOUS
    // login left in storage, because `AuthCallbackPage`'s exchange has not replaced it yet. The POST is a
    // tenant-scoped WRITE, so on a privileged session that is mid-re-authentication the backend answers it
    // with ADR-0021 §6.2's second-factor challenge, and the SPA answers THAT by redirecting for a new
    // authorization — which overwrites the PKCE verifier this document came back with and strands the
    // exchange that was about to deliver the very token carrying the factor. Measured on main
    // (run 41334510350, task 2625efef): this request went out 48 ms before the token with
    // `amr: ["pwd","otp"]` landed, and its 401 drove a third re-authentication nobody answered — the
    // realstack REFUSED / ENROLS case sat on Keycloak's password form until it timed out.
    //
    // Nothing is lost by sitting it out, because this is a DEFERRAL and not a skip: `exchangePending`
    // comes from a store that is told the moment the exchange settles (win or lose), so this effect runs
    // again in THIS document, before the guard below has been claimed — and then the POST carries the
    // token the exchange delivered, which is the only token the answer could have been about. A plain
    // `codeExchangePending()` call here would not do: on a callback that boots on a RESTORED session the
    // person and the tenant are unchanged by the sign-in, so no dependency of this effect ever changes
    // again and the reconciliation would be lost for the life of the document.
    if (exchangePending) return;

    // WHO, and WHERE. A person who switches organisation is reconciled again, in the tenant they switched
    // to, because the row the answer is about is a different row there.
    const reconciling = `${userId}\u0000${tenantKey}`;
    if (reconciledFor.current === reconciling) return;
    reconciledFor.current = reconciling;

    // The language this call sets out with. The answer is ABOUT it — direction 1 seeds the row from it —
    // so when the answer comes back the two have to be compared: a reconciliation that was in flight
    // while the person used the switcher is an answer to a question they have since answered themselves
    // (see languageToApply, and task c4b87c3b for the reload it cost).
    const asked = toStorableLanguage(i18n.language);

    void reconcilePreferredLanguage(asked)
      .then((settled) => {
        const apply = languageToApply({
          asked,
          current: toStorableLanguage(i18n.language),
          settled: settled.preferredLanguage ? toStorableLanguage(settled.preferredLanguage) : null,
          // Read HERE, not when the call went out: the person may have used the switcher at any point up
          // to this moment, and all of those statements are later than the row this answer was read from.
          stated: languageWasStated(),
        });
        if (apply) void i18n.changeLanguage(apply);
      })
      .catch(() => {
        reconciledFor.current = null;
      });
  }, [userId, tenantKey, i18n, exchangePending]);

  return null;
}
